/**
 * Henter ut den mest beskrivende, lesbare feilmeldingen fra en API-feilkropp.
 *
 * Backend (BizClick.WMS.Api ExceptionHandlingMiddleware) returnerer en standardisert
 * feilkropp (RFC-7807-aktig, camelCase):
 *   { statusCode, title, detail, errorCode, details, errors, timestamp }
 * der den menneskelesbare teksten ligger i `detail` (f.eks. "Bin '710101301' finnes
 * ikke i warehouse 1"). Eldre controller-svar bruker `{ error }` (soft-fail /
 * 500-catch) eller `{ errorMessage }` (service-result), og enkelte `{ message }`.
 *
 * Tidligere leste service-laget kun `error`/`errorMessage`, slik at middleware-feil
 * falt tilbake til intetsigende «... 404»-meldinger. Denne hjelperen samler
 * oppslagsrekkefølgen ett sted slik at alle fremtidige feilkropper håndteres likt.
 */
export interface ApiErrorBody {
  /** Lesbar detaljtekst fra ExceptionHandlingMiddleware (foretrukket). */
  detail?: string;
  /** Kort tittel fra middleware (f.eks. "Resource Not Found"). */
  title?: string;
  /** Eldre controller-form: BadRequest({ error }) / 500-catch. */
  error?: string;
  /** Service-result-form: { errorMessage }. */
  errorMessage?: string;
  /** Generisk fallback enkelte endepunkt bruker. */
  message?: string;
  /** Maskinlesbar feilkode (BIN_NOT_FOUND, DUPLICATE_ENTRY, ...). */
  errorCode?: string;
  /**
   * Felt→melding(er). To former: ASP.NET Cores automatiske modellvalidering
   * (ValidationProblemDetails) gir `{ felt: [meldinger] }`, middleware-ens
   * ValidationException gir `{ felt: melding }`.
   */
  errors?: Record<string, string | string[]>;
  [key: string]: unknown;
}

/**
 * Flater ut `errors`-objektet til én lesbar tekst ("melding1. melding2").
 * ASP.NET Cores ValidationProblemDetails har kun en generisk engelsk `title`
 * ("One or more validation errors occurred.") — de faktiske meldingene fra
 * [Required]/[StringLength] ligger i `errors`, så uten denne utflatingen ser
 * brukeren aldri hva som var galt.
 */
function flattenValidationErrors(errors: unknown): string | null {
  if (!errors || typeof errors !== 'object' || Array.isArray(errors)) {
    return null;
  }
  const messages = Object.values(errors as Record<string, unknown>)
    .flatMap(v => (Array.isArray(v) ? v : [v]))
    .filter((v): v is string => typeof v === 'string' && v.trim().length > 0);

  return messages.length > 0 ? messages.join('. ') : null;
}

/** Minste delen av en Response/axios-response vi trenger for status-fallback. */
type StatusBearing = { status?: number; statusText?: string };

/**
 * Returnerer den beste tilgjengelige feilmeldingen fra en parset feilkropp.
 *
 * Rekkefølge: detail → error → errorMessage → message → errors (utflatet) → title.
 * Faller tilbake til `fallbackPrefix` (+ status) når kroppen mangler eller ikke har
 * en brukbar tekst. `errors` prioriteres over `title` fordi ValidationProblemDetails
 * kun har den generiske tittelen mens de reelle meldingene ligger i `errors`.
 *
 * @param body Parset JSON-feilkropp (kan være null hvis responsen manglet/ugyldig JSON).
 * @param response Den feilede responsen (fetch `Response` eller axios `error.response`), brukt til status-fallback.
 * @param fallbackPrefix Valgfri menneskelig prefiks for fallback, f.eks. "Kunne ikke fullføre utplassering".
 */
export function extractApiError(
  body: unknown,
  response?: StatusBearing | null,
  fallbackPrefix?: string
): string {
  // Aksepterer vilkårlig parset feilkropp (også konkrete service-DTO-er uten index-signatur).
  // Vi leser kun valgfrie tekstfelt defensivt, så en intern cast til ApiErrorBody er trygg.
  const b = body as ApiErrorBody | null | undefined;
  const fromBody =
    b?.detail ||
    b?.error ||
    b?.errorMessage ||
    b?.message ||
    flattenValidationErrors(b?.errors) ||
    b?.title;

  if (typeof fromBody === 'string' && fromBody.trim().length > 0) {
    return fromBody;
  }

  const statusPart =
    response && response.status != null
      ? `${response.status}${response.statusText ? ` ${response.statusText}` : ''}`.trim()
      : '';

  if (fallbackPrefix) {
    return statusPart ? `${fallbackPrefix} (${statusPart})` : fallbackPrefix;
  }
  return statusPart || 'Ukjent feil';
}

/**
 * Feil fra API-et med maskinlesbar kode og strukturerte parametre i behold.
 *
 * `message` er identisk med det `extractApiError` returnerer (backend `detail` osv.),
 * så eksisterende kode som leser `error.message` oppfører seg uendret. I tillegg
 * bæres `errorCode` og `details` videre slik at UI-laget kan slå opp en oversatt
 * melding per feilkode (se `translateApiError`). Samme mønster som LockConflictError.
 */
export class ApiError extends Error {
  public readonly errorCode?: string;
  public readonly details?: Record<string, unknown>;

  constructor(message: string, errorCode?: string, details?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.errorCode = errorCode;
    this.details = details;
  }
}

/**
 * Bygger en ApiError fra en parset feilkropp — samme oppslag som `extractApiError`
 * for meldingen, pluss `errorCode`/`details` fra ExceptionHandlingMiddleware-kroppen.
 */
export function createApiError(
  body: unknown,
  response?: StatusBearing | null,
  fallbackPrefix?: string
): ApiError {
  const b = body as ApiErrorBody | null | undefined;
  const details =
    b?.details && typeof b.details === 'object' && !Array.isArray(b.details)
      ? (b.details as Record<string, unknown>)
      : undefined;

  return new ApiError(
    extractApiError(body, response, fallbackPrefix),
    typeof b?.errorCode === 'string' ? b.errorCode : undefined,
    details
  );
}

type TranslateFn = (
  key: string,
  params?: Record<string, string | number | null | undefined>,
  defaultText?: string
) => string;

/**
 * Posisjonelle i18n-parametre ({0}, {1}, ...) per feilkode, bygget fra `details`
 * (camelCase fra backend). Koder uten oppføring her oversettes uten parametre.
 */
const ERROR_CODE_PARAMS: Record<string, (d: Record<string, unknown>) => Record<string, string>> = {
  INSUFFICIENT_STOCK_FOR_CORRECTION: d => ({
    '0': String(d.requestedDeduction ?? '?'),
    '1': String(d.itemNo ?? '?'),
    '2': String(d.binCode ?? '?'),
    '3': String(d.currentQuantity ?? '?')
  }),
  // Fullføring av lokasjonsflytt: saldo på fra-lokasjon dekker ikke linjen
  INSUFFICIENT_STOCK_FOR_MOVE: d => ({
    '0': String(d.requestedQuantity ?? '?'),
    '1': String(d.itemNo ?? '?'),
    '2': String(d.fromBinCode ?? '?'),
    '3': String(d.availableQuantity ?? '?')
  }),
  // Registrering/endring av lokasjonsflytt-linje: saldo minus ventende linjer i
  // samme session dekker ikke linjen
  INSUFFICIENT_STOCK_FOR_MOVE_SESSION: d => ({
    '0': String(d.requestedQuantity ?? '?'),
    '1': String(d.itemNo ?? '?'),
    '2': String(d.fromBinCode ?? '?'),
    '3': String(d.availableQuantity ?? '?'),
    '4': String(d.pendingQuantity ?? '?')
  }),
  // Leveringsmåte-gate ved avslutning av plukk (36372620): ordrene som mangler leveringsmåte
  DELIVERY_METHOD_REQUIRED: d => ({
    '0': Array.isArray(d.orderNos) ? d.orderNos.join(', ') : '?'
  }),
  // Lageroverføring: til-lokasjonen finnes ikke, eller ligger i et annet lager enn valgt til-lager
  TO_LOCATION_NOT_FOUND: d => ({
    '0': String(d.toBinCode ?? '?')
  }),
  TO_LOCATION_NOT_IN_WAREHOUSE: d => ({
    '0': String(d.toBinCode ?? '?'),
    '1': String(d.binWarehouse ?? '?'),
    '2': String(d.toWarehouse ?? '?')
  }),
  // Landkode oppgitt i TA-dialogen (36376022): hvilke ordrer/koder som ble avvist
  INVALID_COUNTRY_CODE: d => ({
    '0': String(d.invalidCodes ?? '?')
  })
};

/**
 * Oversetter en API-feil til brukerens språk via `common.errors.apiCodes.<errorCode>`.
 *
 * Fallback-kjede: oversatt feilkode → backend `detail` (norsk, `error.message`) →
 * `null` (kalleren viser sin generiske melding). Feil som ikke er ApiError gir
 * alltid `null`, så eksisterende oppførsel bevares for ikke-oppgraderte kall.
 */
export function translateApiError(error: unknown, t: TranslateFn): string | null {
  if (!(error instanceof ApiError)) {
    return null;
  }

  if (error.errorCode) {
    const params = ERROR_CODE_PARAMS[error.errorCode]?.(error.details ?? {});
    // error.message (backend detail) som defaultText → brukes når koden mangler oversettelse
    return t(`common.errors.apiCodes.${error.errorCode}`, params, error.message);
  }

  return error.message || null;
}
